(() => {
  'use strict';
  if (customElements.get('solago-topic-guide')) return;

  class SolagoTopicGuide extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      const { signal } = this._controller;
      queueMicrotask(() => {
        if (!signal.aborted && this.isConnected) this.initialize(signal);
      });
    }

    initialize(signal) {
      this._buttons = [...this.querySelectorAll('[data-topic-id]')];
      this._panels = [...this.querySelectorAll('[data-topic-panel]')];
      this._status = this.querySelector('[data-topic-status]');
      const nav = this.querySelector('[data-topic-nav]');
      if (!this._buttons.length || !this._panels.length || !nav) return;

      if (this._mobileTopic === undefined) {
        this._mobileTopic = this.dataset.mobileFirstOpen === 'true' ? this._panels[0].dataset.topicPanel : null;
      }

      nav.setAttribute('role', 'tablist');
      nav.setAttribute('aria-orientation', 'vertical');
      this._buttons.forEach((button, index) => {
        button.setAttribute('role', 'tab');
        button.addEventListener('click', () => this.showTopic(button.dataset.topicId), { signal });
        button.addEventListener('keydown', event => {
          if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this._buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + this._buttons.length) % this._buttons.length;
          this._buttons[next].focus();
          this.showTopic(this._buttons[next].dataset.topicId);
        }, { signal });
      });
      this._panels.forEach(panel => {
        panel.addEventListener('toggle', event => {
          if (event.target !== panel || this._mode !== 'mobile') return;
          const id = panel.dataset.topicPanel;
          if (panel.open) {
            this._mobileTopic = id;
            this._activeTopic = id;
            this._panels.forEach(other => { if (other !== panel) other.open = false; });
          } else if (this._mobileTopic === id) {
            this._mobileTopic = null;
          }
        }, { signal });
      });

      document.addEventListener('shopify:block:select', event => {
        const blockId = event.detail?.blockId;
        if (this._panels.some(panel => panel.dataset.topicPanel === blockId)) this.showTopic(blockId);
      }, { signal });

      const previous = this._activeTopic;
      this._activeTopic = this._buttons.some(button => button.dataset.topicId === previous) ? previous : this._buttons[0].dataset.topicId;
      this.updateMode();
      this.setAttribute('data-enhanced', '');
      this._resizeObserver = new ResizeObserver(() => this.updateMode());
      this._resizeObserver.observe(this);
    }

    updateMode() {
      const mode = this.clientWidth >= 780 ? 'desktop' : 'mobile';
      if (this._mode === mode) return;
      this._mode = mode;
      this._panels.forEach(panel => {
        if (mode === 'desktop') {
          const button = this._buttons.find(item => item.dataset.topicId === panel.dataset.topicPanel);
          panel.setAttribute('role', 'tabpanel');
          if (button) panel.setAttribute('aria-labelledby', button.id);
          panel.open = true;
        } else {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
          panel.hidden = false;
          panel.open = panel.dataset.topicPanel === this._mobileTopic;
        }
      });
      if (mode === 'desktop') this.showTopic(this._activeTopic, false);
    }

    showTopic(id, announce = true) {
      const active = this._buttons.find(button => button.dataset.topicId === id);
      if (!active) return;
      this._activeTopic = id;
      this._buttons.forEach(button => {
        button.setAttribute('aria-selected', String(button === active));
        button.setAttribute('tabindex', button === active ? '0' : '-1');
      });
      this._panels.forEach(panel => {
        panel.hidden = this._mode === 'desktop' && panel.dataset.topicPanel !== id;
        if (this._mode === 'mobile') panel.open = panel.dataset.topicPanel === id;
      });
      if (this._mode === 'mobile') this._mobileTopic = id;
      if (announce && this._status) this._status.textContent = active.textContent.trim();
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._resizeObserver?.disconnect();
      this._controller = null;
      this._mode = null;
    }
  }

  customElements.define('solago-topic-guide', SolagoTopicGuide);
})();
